import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram } from 'lucide-react';
import WhatsAppIcon from '../../ui/WhatsAppIcon';
import logo from '../../../assets/brand/Logotipo-AE-jpg.webp';
import logoCinfop from '../../../assets/brand/CINFOP-LOGO.webp';
import logoCEO from '../../../assets/brand/Logo-CEO.webp';
import esencialLogo from '../../../assets/brand/Esencial Ing Blanco.webp';
import { siteInfo, socialLinks, navLinks, footerInfo, WA_NUMBER } from '../../../data/content';
import styles from './Footer.module.css';

const WA_FOOTER_HREF = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
  'Hola 👋 Me gustaría más información sobre los cursos de inglés de American English.'
)}`;

export default function Footer() {
  return (
    <footer className={styles.footer}>
      {/* Línea roja de acento */}
      <div className={styles.redLine} aria-hidden="true" />

      {/* CTA de contención final — WhatsApp directo (mismo embudo único) */}
      <div className={styles.finalCta}>
        <div className={styles.finalCtaText}>
          <p className={styles.finalCtaTitle}>¿Preferís escribirnos?</p>
          <p className={styles.finalCtaSub}>Un asesor te responde por WhatsApp en minutos, sin compromiso.</p>
        </div>
        <a
          href={WA_FOOTER_HREF}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.finalCtaBtn}
        >
          <WhatsAppIcon size={18} color="#0B1440" />
          Escribinos por WhatsApp
        </a>
      </div>

      {/* Fila de logos institucionales */}
      <div className={styles.logosRow}>
        <div className={styles.logoItem}>
          <img loading="lazy" decoding="async" src={logo} alt="American English" className={styles.logoAE} />
        </div>
        <div className={styles.logoSep} aria-hidden="true" />
        <div className={styles.logoItem}>
          <img loading="lazy" decoding="async" src={logoCinfop} alt="CINFOP" className={styles.logoCinfop} />
        </div>
        <div className={styles.logoSep} aria-hidden="true" />
        <div className={styles.logoItem}>
          <img loading="lazy" decoding="async" src={logoCEO} alt="C.E.O. Grupo Empresarial" className={styles.logoCeo} />
        </div>
        <div className={styles.logoSep} aria-hidden="true" />
        <div className={styles.logoItem}>
          <img loading="lazy" decoding="async" src={esencialLogo} alt="We are Essential Costa Rica" className={styles.logoEsencial} />
        </div>
      </div>

      {/* Grid principal */}
      <div className={styles.grid}>
        {/* Brand */}
        <div className={styles.brand}>
          <img loading="lazy" decoding="async" src={logo} alt="American English For Business" className={styles.brandLogo} />
          <p className={styles.brandTagline}>
            Inglés conversacional en Costa Rica con +{siteInfo.stats.years} años de
            trayectoria y +{siteInfo.stats.locations} sedes formando profesionales bilingües.
          </p>
          <img loading="lazy" decoding="async" src={esencialLogo} alt="We are Essential Costa Rica" className={styles.brandSello} />
          <div className={styles.socialRow}>
            <a
              href={socialLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialBtn}
              aria-label="Facebook"
            >
              <Facebook size={16} />
            </a>
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialBtn}
              aria-label="Instagram"
            >
              <Instagram size={16} />
            </a>
            <a
              href={`https://wa.me/${WA_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.socialBtn} ${styles.socialBtnWa}`}
              aria-label="WhatsApp"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </a>
          </div>
        </div>

        {/* Navegación */}
        <div className={styles.col}>
          <p className={styles.colTitle}>Programas</p>
          {navLinks.map(link => (
            <Link key={link.href} to={link.href} className={styles.colLink}>
              {link.label}
            </Link>
          ))}
        </div>

        {/* Contacto */}
        <div className={styles.col}>
          <p className={styles.colTitle}>Contacto</p>
          <div className={styles.contactItem}>
            <div className={styles.contactIcon}>
              <MapPin size={16} />
            </div>
            <div>
              <span className={styles.contactLabel}>Ubicación</span>
              <span className={styles.contactText}>Costa Rica · +{siteInfo.stats.locations} sedes</span>
            </div>
          </div>
          <div className={styles.contactItem}>
            <div className={styles.contactIcon}>
              <Phone size={16} />
            </div>
            <div>
              <span className={styles.contactLabel}>WhatsApp</span>
              <a
                href={`https://wa.me/${WA_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.contactText}
              >
                {siteInfo.phone}
              </a>
            </div>
          </div>
          <div className={styles.contactItem}>
            <div className={styles.contactIcon}>
              <Mail size={16} />
            </div>
            <div>
              <span className={styles.contactLabel}>Email</span>
              <a href={`mailto:${siteInfo.email}`} className={styles.contactText}>
                {siteInfo.email}
              </a>
            </div>
          </div>
        </div>

        {/* Acreditación */}
        <div className={styles.col}>
          <p className={styles.colTitle}>Acreditación</p>
          <p className={styles.acreditText}>
            Centro formador certificado por el INA. Alineado al Marco Común Europeo de Referencia (MCER). Sello Marca País Costa Rica.
          </p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className={styles.bottom}>
        <p className={styles.copyright}>{footerInfo.copyright}</p>
        <div className={styles.bottomLinks}>
          <Link to="/preguntas-frecuentes" className={styles.termsLink}>Preguntas frecuentes</Link>
          <Link to="/terminos" className={styles.termsLink}>Términos y condiciones</Link>
          <Link to="/privacidad" className={styles.termsLink}>Privacidad</Link>
        </div>
        <p className={styles.devCredit}>{footerInfo.developer}</p>
      </div>
    </footer>
  );
}
